import { useContext, useEffect, useMemo, useRef } from 'react';
import { AutoSizer, List, WindowScroller } from 'react-virtualized';

import ProfileDataContext from '@/context/ProfileDataContext';
import useIsFirstRender from '@/hooks/useIsFirstRender';
import useScreenSize from '@/hooks/useScreenSize';
import ProfileNotRatedCard from '@/shared-components/cards/components/ProfileNotRatedCard';
import ProfileRatingCard from '@/shared-components/cards/components/ProfileRatingCard';
import InfiniteScroll from '@/shared-components/lists/components/InfiniteScroll';

import "./styles/VirtualList.css";

const MOBILE_NAVIGATION_OFFSET_FALLBACK = 110;

export default function VirtualList({ items, visibleItems, onLoadMore }) {
  const { profileData } = useContext(ProfileDataContext);
  const screenSize = useScreenSize();
  const isFirstRender = useIsFirstRender();
  const heightOfRatingCard = useMemo(()=>computeRatingHeight(), [screenSize]);
  const listRef = useRef();
  const heightsCacheRef = useRef({ cacheValidFor: screenSize.width, values: {} });
  const navigationOffsetRef = useRef(screenSize.width <= 650 ? MOBILE_NAVIGATION_OFFSET_FALLBACK : 0);

  if(heightsCacheRef.current.cacheValidFor !== screenSize.width) {
    heightsCacheRef.current = { cacheValidFor: screenSize.width, values: {} };
  }

  function computeRatingHeight() {
    const ratingMeasurer = document.createElement("div");

    ratingMeasurer.innerHTML = "<div class='profile-rating-measurer__container'><div class='profile-rating-measurer__user-img'></div><div class='profile-rating-measurer__artwork-img'></div></div>";
    document.body.appendChild(ratingMeasurer);

    const height = ratingMeasurer.offsetHeight;

    document.body.removeChild(ratingMeasurer);

    return height;
  }

  function getRowGap() {
    if(screenSize.width <= 550) return 12;
    if(screenSize.width <= 1004) return 15;
    return 20;
  }

  function computeRowHeight({ index }) {
    const navigationOffset = screenSize.width <= 650 && index === visibleItems.length - 1 ? navigationOffsetRef.current : 0;
    const spacer = getRowGap() + navigationOffset;

    if(!visibleItems[index].my_rating) {
      const baseHeight = screenSize.width <= 550 ? 85 : screenSize.width <= 1004 ? 102 : 127;
      return baseHeight + spacer;
    }

    const cachedHeight = heightsCacheRef.current.values[index];

    if(cachedHeight) {
      return cachedHeight;
    }

    return heightOfRatingCard + spacer;
  }

  function handleRatedRowMeasure(index, element) {
    if(!element) return;

    const measuredHeight = element.offsetHeight;

    if(heightsCacheRef.current.values[index] !== measuredHeight) {
      heightsCacheRef.current.values[index] = measuredHeight;
      if(listRef.current) {
        listRef.current.recomputeRowHeights(index);
      }
    }
  }

  useEffect(() => {
    if(typeof document !== "undefined") {
      if(screenSize.width > 650) {
        if(navigationOffsetRef.current !== 0) {
          navigationOffsetRef.current = 0;

          if(visibleItems.length > 0) {
            const lastIndex = visibleItems.length - 1;
            delete heightsCacheRef.current.values[lastIndex];
          }

          if(listRef.current) {
            listRef.current.recomputeRowHeights();
          }
        }
      } else {
        const navElement = document.querySelector('.app-navigation__container-outter');
        const measuredHeight = navElement?.getBoundingClientRect().height;
        const nextValue = measuredHeight && measuredHeight > 0 ? measuredHeight : MOBILE_NAVIGATION_OFFSET_FALLBACK;

        if(navigationOffsetRef.current !== nextValue) {
          navigationOffsetRef.current = nextValue;

          if(visibleItems.length > 0) {
            const lastIndex = visibleItems.length - 1;
            delete heightsCacheRef.current.values[lastIndex];
          }

          if(listRef.current) {
            listRef.current.recomputeRowHeights();
          }
        }
      }
    }

    if(!isFirstRender) {
      heightsCacheRef.current = { cacheValidFor: screenSize.width, values: {} };
      listRef.current.recomputeRowHeights();
    }
  }, [screenSize.width]);

  useEffect(() => {
    heightsCacheRef.current.values = {};
    if(listRef.current) {
      listRef.current.recomputeRowHeights();
    }
  }, [visibleItems.length]);

  return (
    <>
      <InfiniteScroll 
        className="profile-profile-ratings__ratings-infinite-scroll" 
        onLoadMore={onLoadMore} 
        shouldLoadMore={items.data.length < items.totalDocs} 
        offsetElements={3} 
        listLength={visibleItems.length}
      >
        {({ infiniteScrollAssigner }) => (
          <WindowScroller>
            {({ height, isScrolling, onChildScroll, scrollTop }) => (
              <AutoSizer disableHeight>
                {({ width }) => (
                  <List
                    autoHeight
                    width={width === 0 ? 0.1 : width}
                    height={height}
                    isScrolling={isScrolling}
                    onScroll={onChildScroll}
                    rowCount={visibleItems.length}
                    rowHeight={computeRowHeight}
                    estimatedRowSize={400}
                    scrollTop={scrollTop}
                    overscanRowCount={5}
                    ref={listRef}
                    rowRenderer={({ index, key, style }) => {
                      const navigationOffset = screenSize.width <= 650 && index === visibleItems.length - 1 ? navigationOffsetRef.current : 0;
                      const rowGap = getRowGap() + navigationOffset;
                      const itemHasRating = Boolean(visibleItems[index].my_rating);

                      return (
                        <div
                          key={key}
                          style={{ ...style, overflow: 'visible' }}
                          ref={infiniteScrollAssigner(index)}
                        >
                          {itemHasRating ? (
                            <div
                              ref={(element) => handleRatedRowMeasure(index, element)}
                              style={{ paddingBottom: rowGap }}
                            >
                              <ProfileRatingCard data={visibleItems[index]} ratingUser={profileData}/>
                            </div>
                          ) : (
                            <div style={{ paddingBottom: rowGap }}>
                              <ProfileNotRatedCard data={visibleItems[index]} ratingUser={profileData}/>
                            </div>
                          )}
                        </div>
                      );
                    }}
                  />
                )}
              </AutoSizer>
            )}
          </WindowScroller>
        )}
      </InfiniteScroll>
    </>
  );
}
